import DataGrid from "../../components/DataGrid";
import { useCallback, useEffect, useMemo } from "react";
import { deliveriesConfig as config } from "./deliveriesConfig";
import TableTitleRow from "../../components/TableTitleRow";
import useVisibleColumns from "../../hooks/useVisibleColumns";
import { Delivery } from "../../types/delivery";
import { api } from "../../api";
import { deliveryStatusOptions } from "../../helpers/deliveryStatuses";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { deliveriesSearchFieldMap } from "../../helpers/searchFieldMap";
import { deliveriesSortFieldMap } from "../../helpers/sortFieldMap";
import { ViewProvider } from "../../contexts/ViewContext";
import { deliveryFromExternalList } from "../../hooks/useSetDataByView/convert";
import { TableDialogs } from "../../components/TableDialogs";
import { getIsNationalFromUrl } from "../../helpers/isNational";
import { extractApiError } from "../../api/errors";
import { useDataGridQuery, useUrlState } from "../../hooks/useUrlState";
import { DELIVERIES_DEFAULTS } from "../../helpers/defaultUrl";
import { useSearchParams } from "react-router";
import { ConfirmDialog } from "primereact/confirmdialog";
import { getVisibleSearchFields } from "../../helpers/getVisibleSearchFields";
import {
  DataTablePageEvent,
  DataTableRowReorderEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { useShowToast } from "../../contexts/ToastContext.tsx";
import { DeliveriesDropdowns } from "../../types";
import {
  cityCountryPairLabel,
  cityCountryPairValue,
  parseCityCountryPairValue,
} from "../../helpers/cityCountryPair.ts";
import { useDeliveryReorder } from "../../hooks/useDeliveryReorder";

const columns = config.columns;

export default function DeliveriesView() {
  const {
    filters,
    dateFrom,
    dateTo,
    globalFilterValue,
    setGlobalFilterValue,
    showCompletedRows,
  } = useUrlState();

  const { page, limit, sort, order, onPageChange, onSortChange } =
    useDataGridQuery({
      page: DELIVERIES_DEFAULTS.page,
      limit: DELIVERIES_DEFAULTS.limit,
      sort: DELIVERIES_DEFAULTS.sort,
      order: DELIVERIES_DEFAULTS.order,
    });

  const [searchParams] = useSearchParams();

  const [visibleColumns, setVisibleColumns] = useVisibleColumns(columns);
  const debouncedSearch = useDebouncedValue(globalFilterValue, 300);
  const showToast = useShowToast();

  // map visible columns to search fields
  const visibleSearchFields = useMemo(
    () => getVisibleSearchFields(visibleColumns, deliveriesSearchFieldMap),
    [visibleColumns],
  );

  //sorting
  const orderByField = useMemo(() => {
    const mapped = deliveriesSortFieldMap[sort];
    const base = mapped ?? DELIVERIES_DEFAULTS.sort;
    const direction = order === -1 ? "-" : "";
    return `${direction}${base}`;
  }, [sort, order]);

  const rankOrderByField = useMemo(() => {
    if (orderByField === "-date") {
      return "-sort_rank";
    }
    return "sort_rank";
  }, [orderByField]);

  const filteredLoadingCityAndCountry = useMemo(() => {
    if (!filters.loadingCity) {
      return undefined;
    }
    return parseCityCountryPairValue(filters.loadingCity);
  }, [filters.loadingCity]);

  const filteredUnloadingCityAndCountry = useMemo(() => {
    if (!filters.unloadingCity) {
      return undefined;
    }
    return parseCityCountryPairValue(filters.unloadingCity);
  }, [filters.unloadingCity]);

  const {
    data: rawData,
    isLoading,
    isFetching,
    error,
  } = api.endpoints.listDeliveriesApiDeliveriesGet.useQuery({
    orderBy: `${orderByField},${rankOrderByField},-created_at`,
    limit: limit,
    offset: page * limit,
    search: debouncedSearch || undefined,
    searchFields: visibleSearchFields,
    // filters
    status: filters.deliveryStatus,
    driverId: filters.driver,
    driverManagerId: filters.driverManager,
    isNational: getIsNationalFromUrl(searchParams),
    entryStatus: !showCompletedRows ? "default,pending" : undefined,
    dateFrom: dateFrom ? dateFrom.toISOString().split("T")[0] : undefined,
    dateTo: dateTo ? dateTo.toISOString().split("T")[0] : undefined,
    loadingCity: filteredLoadingCityAndCountry?.city,
    loadingCountry: filteredLoadingCityAndCountry?.country,
    unloadingCity: filteredUnloadingCityAndCountry?.city,
    unloadingCountry: filteredUnloadingCityAndCountry?.country,
  });

  useEffect(() => {
    if (error) {
      showToast({
        severity: "error",
        summary: "Błąd ładowania danych",
        detail: extractApiError(error),
      });
    }
  }, [error, showToast]);

  const data: Delivery[] = useMemo(() => {
    if (!rawData) {
      return [];
    }
    return rawData.items.map(deliveryFromExternalList);
  }, [rawData]);

  const { data: drivers } = api.endpoints.listDriversApiDriversGet.useQuery({
    limit: 10000,
    orderBy: "last_name",
  });
  const { data: cities } =
    api.endpoints.listCitiesApiLocationsCitiesGet.useQuery({
      limit: 10000,
      orderBy: "city",
    });
  const { data: organizationalUsers } =
    api.endpoints.getOrganizationalUsersApiOrganizationalUsersGet.useQuery({
      limit: 10000,
    });
  const { data: products } = api.endpoints.listProductsApiProductsGet.useQuery({
    limit: 10000,
    orderBy: "name",
  });

  const dropdownConfigs = useMemo<DeliveriesDropdowns>(() => {
    return {
      drivers:
        drivers?.items.map((driver) => ({
          label: `${driver.first_name} ${driver.last_name}`,
          value: driver.id,
          data: driver,
        })) ?? [],
      deliveryStatus: deliveryStatusOptions,
      cities:
        cities?.items.map((city) => ({
          label: cityCountryPairLabel(city),
          value: cityCountryPairValue(city),
          data: city,
        })) ?? [],
      organizationalUsers:
        organizationalUsers?.items?.map((user) => ({
          label: user.email,
          value: user.id,
        })) ?? [],
      products:
        products?.items?.map((item) => ({
          label: item.name,
          value: item.id,
          data: item,
        })) ?? [],
    };
  }, [drivers, cities, organizationalUsers, products]);

  const handleSortChange = useCallback(
    (e: DataTableSortEvent) => onSortChange(e.sortField, e.sortOrder ?? 1),
    [onSortChange],
  );
  const handlePageChange = useCallback(
    (e: DataTablePageEvent) => onPageChange(e.page ?? 0, e.rows ?? limit),
    [onPageChange, limit],
  );

  // Reordering logic
  const { calculateNewSortRank } = useDeliveryReorder({
    ascending: rankOrderByField === "sort_rank",
    offset: page * limit,
  });
  const [updateDelivery] =
    api.endpoints.updateDeliveryApiDeliveriesSourceDeliveryIdPut.useMutation();

  // Only enable reordering when sorted by date (primary sort field)
  const isReorderingEnabled = useMemo(() => {
    const mappedSort = deliveriesSortFieldMap[sort] ?? sort;
    return mappedSort === "date";
  }, [sort]);

  const handleRowReorder = useMemo(
    () =>
      isReorderingEnabled
        ? (e: DataTableRowReorderEvent<Delivery[]>) => {
            /* Drag index coming from DataTableRowReorderEvent is a global index
             * within the whole set of entries on all pages, so on pages greater
             * than 0 it will be offset by page start. Data, on the other hand,
             * is data for the current page only. */
            const draggedDelivery = data[-page * limit + e.dragIndex];
            if (!draggedDelivery) {
              /* This shouldn't normally happen and would indicate some kind
               * of a bug, likely related to inconsistency between data table
               * and data due to key issues.*/
              console.error(
                `Failed attempt to get delivery with index ${e.dragIndex}
                on page ${page} with limit ${limit} while length of 
                data array is ${data.length}`,
              );
              showToast({
                severity: "warn",
                summary: "Nieprawidłowa operacja",
                detail: "Nie można przenieść dostawy",
              });
              return;
            }
            const { newSortRank, newDate, isValidDrop, errorMessage } =
              calculateNewSortRank({
                draggedDelivery,
                dropIndex: e.dropIndex,
                allDeliveries: data,
              });

            if (!isValidDrop) {
              showToast({
                severity: "warn",
                summary: "Nieprawidłowa operacja",
                detail: errorMessage || "Nie można przenieść dostawy",
              });
              return;
            }

            // Only update if sort_rank or date changed
            const isSortRankChanged = newSortRank !== draggedDelivery.sortRank;
            const isDateChanged = newDate !== undefined;

            if (!isSortRankChanged && !isDateChanged) {
              return;
            }

            // Prepare update payload
            const deliveryUpdate: {
              sort_rank: number;
              date?: string | null;
            } = {
              sort_rank: newSortRank,
            };

            // Add date to update if it changed
            if (isDateChanged) {
              deliveryUpdate.date = newDate
                ? newDate.toISOString().split("T")[0]
                : null;
            }

            // Update via API
            updateDelivery({
              source: draggedDelivery.source,
              deliveryId: draggedDelivery.objectId,
              deliveryUpdate,
            })
              .then(({ error }) => {
                if (error) {
                  showToast({
                    severity: "error",
                    summary: "Błąd aktualizacji",
                    detail: extractApiError(error),
                  });
                  return;
                }
                const message = isDateChanged
                  ? "Dostawa została przeniesiona do innej daty"
                  : "Kolejność dostaw została zaktualizowana";
                showToast({
                  severity: "success",
                  summary: "Sukces",
                  detail: message,
                });
              })
              .catch(() =>
                showToast({
                  severity: "error",
                  summary: "Błąd aktualizacji",
                  detail: "Nie udało się zaktualizować dostawy",
                }),
              );
          }
        : undefined,
    [
      isReorderingEnabled,
      data,
      page,
      limit,
      calculateNewSortRank,
      updateDelivery,
      showToast,
    ],
  );

  return (
    <ViewProvider view={config.view}>
      <TableDialogs />
      <ConfirmDialog />
      <TableTitleRow<DeliveriesDropdowns, "deliveries">
        visibleColumns={visibleColumns}
        columns={columns}
        setVisibleColumns={setVisibleColumns}
        globalFilterValue={globalFilterValue}
        setGlobalFilterValue={setGlobalFilterValue}
        config={config}
        dropdownConfigs={dropdownConfigs}
      />
      <DataGrid<DeliveriesDropdowns, "deliveries">
        data={data}
        page={page}
        onPage={handlePageChange}
        rowsPerPage={limit}
        totalRecords={rawData?.total || 0}
        sortField={sort}
        sortOrder={order}
        onSort={handleSortChange}
        visibleColumns={visibleColumns}
        config={config}
        dropdownConfigs={dropdownConfigs}
        globalFilterValue={globalFilterValue}
        isLoading={isLoading}
        isFetching={isFetching}
        noStatusButtons
        reorderableRows={isReorderingEnabled}
        onRowReorder={handleRowReorder}
        showRowReorder
      ></DataGrid>
    </ViewProvider>
  );
}
